import { useQuery } from '@apollo/client';
import { useSelector } from '@xstate/store-react';
import isNil from 'lodash/isNil';
import { PropsWithChildren, useEffect, useState } from 'react';
import LoadingScreen from '@/components/LoadingScreen';
import {
  useHydrateSessionStore,
  useIdentifyPosthogUser,
  useLogoutOnIdle,
  useUpscopeConnection,
} from '@/components/session/hooks';
import { GET_SESSION_DATA } from '@/graphql/Users.queries';
import { useAccessToken } from './AccessTokenContext';
import { sessionStore } from './session.store';

type SessionInitializerProps = PropsWithChildren<{
  setClientCacheAndNetworkPolicy: React.Dispatch<React.SetStateAction<boolean>>;
}>;

export function SessionInitializer({ children, setClientCacheAndNetworkPolicy }: SessionInitializerProps) {
  const { isLoadingAccessToken } = useAccessToken();

  const { data, error } = useQuery(GET_SESSION_DATA, {
    skip: isLoadingAccessToken,
    fetchPolicy: 'network-only',
    pollInterval: 5 * 60 * 1000,
  });

  // Effects below are ordered intentionally. React runs effects bottom-up (children before
  // parents), so children are safe to render only once a parent effect has updated the store.
  // Within this component, effects fire in declaration order. useHydrateSessionStore must
  // run before networkHydrationDone is set, so the store is fresh when children first render.
  useHydrateSessionStore(data, error);
  useIdentifyPosthogUser();
  useUpscopeConnection();
  useLogoutOnIdle();

  useEffect(() => {
    const sessionData = data?.getSessionData;
    if (isNil(sessionData)) return;
    setClientCacheAndNetworkPolicy(sessionData.enabledFeatureFlags.includes('CLIENT_CACHE_AND_NETWORK_POLICY'));
  }, [data, setClientCacheAndNetworkPolicy]);

  // Declared LAST so it fires after useHydrateSessionStore has already updated the session
  // store with fresh network data. Once true it stays true, so polls don't remount children.
  const [networkHydrationDone, setNetworkHydrationDone] = useState(false);
  useEffect(() => {
    if (!isNil(data) || !isNil(error)) {
      setNetworkHydrationDone(true);
    }
  }, [data, error]);

  const status = useSelector(sessionStore, (s) => s.context.status);

  if (status === 'loading' || status === 'unauthenticated' || !networkHydrationDone) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}
